import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Download, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  nuova: "Nuova",
  "in-valutazione": "In valutazione",
  contattata: "Contattata",
  accettata: "Accettata",
  archiviata: "Archiviata",
};

const STATUS_COLORS = {
  nuova: "bg-gold/15 text-gold border-gold/40",
  "in-valutazione": "bg-ink/5 text-ink border-ink/20",
  contattata: "bg-sand text-stone border-line",
  accettata: "bg-[#3E7C4F]/10 text-[#3E7C4F] border-[#3E7C4F]/30",
  archiviata: "bg-cream text-stone/60 border-line",
};

const inputCls =
  "w-full px-4 py-3 rounded-lg bg-cream border border-line focus:border-gold focus:outline-none text-ink text-sm";

export default function AdminSkal({ headers }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [notesDraft, setNotesDraft] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/admin/skal/candidature`, { headers })
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Caricamento candidature non riuscito."))
      .finally(() => setLoading(false));
  };

  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (item, status) => {
    try {
      await axios.patch(`${API}/admin/skal/candidature/${item.id}`, { status }, { headers });
      setItems((list) => list.map((x) => (x.id === item.id ? { ...x, status } : x)));
      toast.success(`Stato aggiornato: ${STATUS_LABELS[status]}`);
    } catch {
      toast.error("Aggiornamento stato non riuscito.");
    }
  };

  const saveNotes = async (item) => {
    setSavingId(item.id);
    try {
      const notes = notesDraft[item.id] ?? item.notes ?? "";
      await axios.patch(`${API}/admin/skal/candidature/${item.id}`, { notes }, { headers });
      setItems((list) => list.map((x) => (x.id === item.id ? { ...x, notes } : x)));
      toast.success("Nota salvata.");
    } catch {
      toast.error("Salvataggio nota non riuscito.");
    } finally {
      setSavingId(null);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${API}/admin/skal/candidature-export`, {
        headers,
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "candidature-skal-albania.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Esportazione non riuscita.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="admin-skal">
      <div className="flex items-center justify-between gap-4 mb-6">
        <h2 className="font-serif text-2xl text-ink">Candidature Skål Albania ({items.length})</h2>
        <button
          onClick={exportCsv}
          data-testid="admin-skal-export"
          disabled={exporting || items.length === 0}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ink text-cream text-sm font-semibold hover:bg-gold disabled:opacity-50 transition-colors duration-300"
        >
          {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
          Esporta CSV
        </button>
      </div>

      {loading ? (
        <p className="text-stone" data-testid="admin-skal-loading">Caricamento candidature…</p>
      ) : items.length === 0 ? (
        <p className="text-stone" data-testid="admin-skal-empty">
          Nessuna candidatura ricevuta finora. Le nuove candidature dalla pagina Skål Albania compariranno qui.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {items.map((c) => {
            const open = openId === c.id;
            return (
              <div key={c.id} data-testid={`admin-skal-item-${c.id}`} className="p-5 rounded-xl border border-line bg-cream">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <p className="font-serif text-lg text-ink">
                        {c.first_name} {c.last_name}
                      </p>
                      <span className={`text-[10px] uppercase tracking-[0.16em] font-semibold px-2.5 py-1 rounded-full border ${STATUS_COLORS[c.status] || STATUS_COLORS.nuova}`}>
                        {STATUS_LABELS[c.status] || c.status}
                      </span>
                    </div>
                    <p className="text-xs text-stone mt-1">
                      {c.role} · {c.sector} · {c.city_country} · {(c.created_at || "").slice(0, 10)}
                    </p>
                    <p className="text-xs text-stone mt-0.5">
                      {c.email} · {c.phone} · pref: {c.contact_preference}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <select
                      value={c.status}
                      onChange={(e) => setStatus(c, e.target.value)}
                      data-testid={`admin-skal-status-${c.id}`}
                      className="px-3 py-2 rounded-lg bg-sand border border-line text-sm text-ink focus:border-gold focus:outline-none"
                    >
                      {Object.entries(STATUS_LABELS).map(([v, l]) => (
                        <option key={v} value={v}>{l}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => setOpenId(open ? null : c.id)}
                      data-testid={`admin-skal-expand-${c.id}`}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-stone hover:text-gold transition-colors duration-300"
                    >
                      {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      Dettagli
                    </button>
                  </div>
                </div>

                {open && (
                  <div className="mt-5 pt-5 border-t border-line grid grid-cols-1 gap-4">
                    {c.organization ? (
                      <p className="text-sm text-stone"><strong className="text-ink">Azienda:</strong> {c.organization}</p>
                    ) : null}
                    <p className="text-sm text-stone"><strong className="text-ink">Anni di esperienza:</strong> {c.experience_years}</p>
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] font-semibold text-stone mb-1.5">Esperienza</p>
                      <p className="text-sm text-ink leading-relaxed whitespace-pre-line">{c.experience}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] font-semibold text-stone mb-1.5">Motivazione</p>
                      <p className="text-sm text-ink leading-relaxed whitespace-pre-line">{c.motivation}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] font-semibold text-stone mb-1.5">Note private</p>
                      <textarea
                        rows={3}
                        value={notesDraft[c.id] ?? c.notes ?? ""}
                        onChange={(e) => setNotesDraft((d) => ({ ...d, [c.id]: e.target.value }))}
                        data-testid={`admin-skal-notes-${c.id}`}
                        placeholder="Note interne visibili solo a te…"
                        className={`${inputCls} resize-y`}
                      />
                      <button
                        onClick={() => saveNotes(c)}
                        disabled={savingId === c.id}
                        data-testid={`admin-skal-save-notes-${c.id}`}
                        className="mt-2 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ink text-cream text-xs font-semibold hover:bg-gold disabled:opacity-50 transition-colors duration-300"
                      >
                        {savingId === c.id ? <Loader2 size={13} className="animate-spin" /> : null}
                        Salva nota
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
